/**
 * ImportCodeModal
 * Import: paste share code, auto-detect, preview config, conflict resolution
 */

import React, { useState, useCallback, useEffect } from 'react';
import { TemplateConfig } from '@/types/template.types';
import { useShareCode } from '@hooks/useShareCode';
import { Modal, Button, Badge } from '../ui';
import { Download, AlertTriangle, CheckCircle2, Eye } from 'lucide-react';

interface ImportCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (config: TemplateConfig) => void;
}

type ImportAction = 'apply' | null;

export const ImportCodeModal: React.FC<ImportCodeModalProps> = ({ isOpen, onClose, onApply }) => {
  const { decode, decodeError } = useShareCode();
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState<{
    config: TemplateConfig;
    name?: string;
    version: number;
    timestamp: number;
  } | null>(null);
  const [applied, setApplied] = useState(false);

  // Auto-detect on paste
  const handleCodeChange = useCallback((value: string) => {
    setCode(value);
    setPreview(null);
    setApplied(false);

    if (value.trim().startsWith('HH1:')) {
      const result = decode(value.trim());
      if (result) {
        setPreview(result);
      }
    }
  }, [decode]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCode('');
      setPreview(null);
      setApplied(false);
    }
  }, [isOpen]);

  const handleApply = useCallback(() => {
    if (!preview) return;
    onApply(preview.config);
    setApplied(true);
    setTimeout(() => onClose(), 800);
  }, [preview, onApply, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Import Configuration">
      <div className="space-y-4">
        {/* Input */}
        <div>
          <label htmlFor="import-share-code" className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2 block">
            Paste Share Code
          </label>
          <textarea
            id="import-share-code"
            value={code}
            onChange={e => handleCodeChange(e.target.value)}
            placeholder="Paste HH1:... code here"
            className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-[11px] text-slate-300 font-mono resize-none h-20 focus:outline-none focus:border-accent/50 placeholder:text-slate-700"
            autoFocus
          />
        </div>

        {/* Error */}
        {decodeError && code.length > 4 && (
          <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800/50 rounded-xl">
            <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
            <p className="text-xs text-red-400">{decodeError}</p>
          </div>
        )}

        {/* Preview */}
        {preview && (
          <div className="space-y-3 animate-in slide-in-from-top-2 duration-200">
            <div className="flex items-center gap-2">
              <Eye size={14} className="text-cyan-400" />
              <span className="text-xs font-black text-slate-300 uppercase tracking-tight">Config Preview</span>
              {preview.name && <Badge variant="info" size="xs">{preview.name}</Badge>}
            </div>

            <div className="bg-slate-950/60 rounded-xl p-3 border border-slate-800/50 space-y-2">
              {/* Vocal Config */}
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-slate-400 uppercase w-20">Vocal</span>
                <Badge variant="primary" size="xs">
                  {preview.config.vocalConfig.mode === 'blend' ? 'Blend Mode' : preview.config.vocalConfig.singlePackKey || 'Default'}
                </Badge>
              </div>

              {/* Blends */}
              {preview.config.musicalBlend.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-bold text-slate-400 uppercase w-20">Genres</span>
                  {preview.config.musicalBlend.map(b => (
                    <Badge key={b} variant="default-muted" size="xs">{b}</Badge>
                  ))}
                </div>
              )}

              {/* Moods */}
              {preview.config.moodArchitecture.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-bold text-slate-400 uppercase w-20">Moods</span>
                  {preview.config.moodArchitecture.map(m => (
                    <Badge key={m} variant="default-muted" size="xs">{m}</Badge>
                  ))}
                </div>
              )}

              {/* Production */}
              {preview.config.production && (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-slate-400 uppercase w-20">Production</span>
                  {preview.config.production.selectedKey && (
                    <Badge variant="info" size="xs">
                      {preview.config.production.selectedKey.root} {preview.config.production.selectedKey.mode}
                    </Badge>
                  )}
                  <Badge variant="default-muted" size="xs">{preview.config.production.selectedTempo} BPM</Badge>
                </div>
              )}

              {/* Timestamp */}
              {preview.timestamp > 0 && (
                <p className="text-[11px] text-slate-700 mt-1">
                  Created: {new Date(preview.timestamp * 1000).toLocaleString()}
                </p>
              )}
            </div>

            {/* Apply Button */}
            <Button
              variant={applied ? 'success' : 'primary'}
              size="sm"
              onClick={handleApply}
              disabled={applied}
              className="w-full !rounded-xl !text-xs !font-black !uppercase !tracking-widest"
            >
              {applied ? (
                <><CheckCircle2 size={14} className="mr-1.5" /> Applied!</>
              ) : (
                <><Download size={14} className="mr-1.5" /> Apply Configuration</>
              )}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};
